"use client";

import { useLazyQuery } from "@apollo/client";
import {
	CalculatePriceDocument,
	type CalculatePriceQuery,
	type CalculatePriceQueryVariables,
} from "@graphql";
import { type FurnitureConfiguratorProps, Loader } from "@tosize/tosize-furniture-configurator";
import { Dialog, type DialogProps } from "@tosize/ui";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { Slot } from "radix-ui";
import { type ReactNode, useMemo, useState } from "react";
import TagManager from "react-gtm-module";
import { updateCustomerProjectItem } from "~/app/[lang]/(default)/(auth)/actions";
import { useCabinet } from "~/app/[lang]/(default)/product/[id]/variant/Cabinets/Context";
import { createShareCode } from "~/app/[lang]/(default)/product/[id]/variant/Cabinets/actions";
import { updateItem } from "~/app/[lang]/cart/actions/item/update";
import { useAddToCart } from "~/app/[lang]/cart/utils/useAddToCart";
import type { ServerFurnitureConfiguratorProps } from "~/components/configurator/utilities";
import type { PriceDisplay as PriceDisplayGenql } from "~/graphql/genql/magento";
import { useBetterAction } from "~/hooks/useBetterAction";

const TosizeFurnitureConfiguratorComponent = dynamic(
	() => import("@tosize/tosize-furniture-configurator"),
	{ ssr: false, loading: () => <Loader mode="add" /> },
);

export interface BaseFurnitureConfiguratorProps
	extends Pick<FurnitureConfiguratorProps, "mode" | "onStoreChange"> {
	wishlistButton?: ReactNode;
	item?: {
		id?: string;
		quantity?: number;
		orderData?: string;
		product?: {
			id?: string;
			priceDisplay?: PriceDisplayGenql;
			isOnWishlist?: boolean;
		};
	};
}

export type VariantFurnitureConfiguratorProps =
	| {
			variant: "cabinet" | "default";
			open?: never;
			onOpenChange?: never;
			title?: never;
			children?: never;
	  }
	| {
			variant: "dialogButton";
			open?: never;
			onOpenChange?: never;
			title: DialogProps["title"];
			children: ReactNode;
	  }
	| {
			variant: "dialog";
			open: DialogProps["open"];
			onOpenChange: DialogProps["onOpenChange"];
			title: DialogProps["title"];
			children?: never;
	  };

type Props = ServerFurnitureConfiguratorProps &
	BaseFurnitureConfiguratorProps &
	VariantFurnitureConfiguratorProps;

export function BaseFurnitureConfigurator(props: Props) {
	const {
		wishlistButton,
		item,

		variant,
		open,
		onOpenChange,
		title,
		children,

		cartMessages,
		customerToken,

		lang,
		messages,
		routes,
		settings,
		data,
		customer,

		mode,
		onStoreChange,
	} = props;

	const { openSampleDialog, setOpenSampleDialog } = useCabinet();
	const searchParams = useSearchParams();

	const productId = useMemo(() => {
		const productId = props.data?.saleableProductId ?? item?.product?.id;

		if (productId === undefined) {
			throw new Error("productId is undefined");
		}

		return typeof productId === "number" ? productId.toString() : productId;
	}, [props.data?.saleableProductId, item?.product?.id]);

	const { executeAsync, isPending } = useBetterAction(updateItem);
	const { handleAddItem, addingItem } = useAddToCart(cartMessages);

	const [calculatePrice] = useLazyQuery<CalculatePriceQuery, CalculatePriceQueryVariables>(
		CalculatePriceDocument,
		{ context: { headers: customerToken ? { "X-API-Key": customerToken } : {} } },
	);

	const [innerOpen, onInnerOpenChange] = useState(false);

	const content = (
		<TosizeFurnitureConfiguratorComponent
			wishlistButton={wishlistButton}
			lang={lang}
			messages={messages}
			routes={routes}
			settings={settings}
			data={data}
			customer={customer ? { name: customer.name, email: customer.email } : undefined}
			dialog={variant.includes("dialog") ? true : undefined}
			mode={mode}
			onStoreChange={onStoreChange}
			quantity={item?.quantity}
			orderData={item?.orderData}
			material={searchParams.get("material")}
			thickness={searchParams.get("thickness") ? Number(searchParams.get("thickness")) : null}
			calculatePrice={async (data) => {
				const response = await calculatePrice({ variables: { productId, data } });

				if (!response.data?.calculatePrice.includingTax || response.error) {
					return "error";
				}

				if (response.data?.calculatePrice) {
					return response.data.calculatePrice;
				}

				return "calculating";
			}}
			onAddToCart={async (addToCartData, quantity) => {
				if (mode === "editCartItem") {
					if (!item?.id) {
						throw new Error("Item ID is missing");
					}

					await executeAsync({
						lang: props.lang,
						cartItem: {
							id: item.id,
							data: addToCartData,
							quantity,
						},
					});
				} else if (mode === "editProjectItem") {
					if (!item?.id) {
						throw new Error("Item ID is missing");
					}

					await updateCustomerProjectItem({
						lang: props.lang,
						productId: productId,
						quantity,
						orderData: addToCartData,
						id: item.id,
					});
				} else {
					await handleAddItem({
						lang: props.lang,
						cartItem: {
							id: productId,
							data: addToCartData,
							quantity,
						},
					});
				}

				onOpenChange?.(false);
			}}
			onAddSampleToCart={async (sampleProductId) => {
				await handleAddItem({
					lang: props.lang,
					cartItem: {
						id: sampleProductId,
						quantity: 1,
						data: JSON.stringify({ product: sampleProductId }),
					},
				});
			}}
			onSave={async (input) => {
				const code = await createShareCode({ lang: props.lang, productId, ...input });

				if (code?.data) {
					TagManager.dataLayer({
						dataLayer: {
							event: "share_design",
							ecommerce: {
								currency: input.price?.currency,
								value: input.price?.excludingTax,
							},
						},
					});
				}

				return code;
			}}
			openSampleDialog={openSampleDialog}
			setOpenSampleDialog={setOpenSampleDialog}
		/>
	);

	if (variant === "dialogButton") {
		return (
			<>
				<Slot.Root
					onClick={(e) => {
						e.stopPropagation();
						onInnerOpenChange(!innerOpen);
					}}
				>
					{children}
				</Slot.Root>
				<Dialog
					width="full"
					height="full"
					open={addingItem || isPending ? true : innerOpen}
					onOpenChange={onInnerOpenChange}
					title={title}
					className="z-dialog"
				>
					<div className="h-full [--canvas-y:calc(var(--window-y,100dvh)-var(--modal-header-y,0px)-var(--spacing)-2rem)]">
						{content}
					</div>
				</Dialog>
			</>
		);
	}

	if (variant === "dialog") {
		return (
			<Dialog width="full" height="full" open={open} onOpenChange={onOpenChange} title={title}>
				<div className="h-full [--canvas-y:calc(var(--window-y,100dvh)-var(--modal-header-y,0px)-var(--spacing)-2rem)]">
					{content}
				</div>
			</Dialog>
		);
	}

	return content;
}
